import { Dispatch, SetStateAction, useState } from 'react'
import { DialogTrigger } from 'react-aria-components'
import { Link, Outlet } from 'react-router-dom'
import type { GenericObject, ObjectProperties } from '../../shared/types'
import { useObjectActionMutationVariable, useObjectList } from '../api/object'
import { Button, ButtonLink } from '../components/button'
import { ClientFormatter } from '../components/client-formatter'
import { CurrencyFormatter } from '../components/currency-formatter'
import { DateFormatter } from '../components/date-formatter'
import { dateRangeToPrimitive, DateRange } from '../components/daterange-picker'
import { Input } from '../components/input'
import { LocationFormatter } from '../components/location-formatter'
import { Paper } from '../components/paper'
import { ProposalVehicleFormatter } from '../components/proposal-vehicle-formatter'
import { Sidepanel } from '../components/sidepanel'
import { SourceFormatter } from '../components/source-formatter'
import { FilteringSidepanel } from '../objects/filtering-sidepanel'
import { Views } from '../objects/views'
import { List, useList } from '../scaffolding/list'
import { ListHeader } from '../scaffolding/list-header'
import { STATUS, STATUS_STYLES } from './const'

export type ParamsType = {
	date_from?: DateRange
	date_to?: DateRange
	volume_from?: string | number
	volume_to?: string | number
	client_search?: string
} & ObjectProperties

// Zobrazí rozsah min–max; pokud jsou hodnoty shodné, jen jedno číslo.
function CurrencyRange({ min, max }: { min?: string | number; max?: string | number }) {
	const a = min == null || min === '' ? null : Number(min)
	const b = max == null || max === '' ? null : Number(max)
	if (a === null && b === null) return null
	if (a === b) return <CurrencyFormatter value={a as number} />
	return (
		<>
			<CurrencyFormatter value={min} />
			<br />
			<CurrencyFormatter value={max} />
		</>
	)
}

const filterToPrimitive = ({ date_from, date_to, ...rest }: ParamsType) => ({
	...rest,
	date_from: date_from ? dateRangeToPrimitive(date_from) : undefined,
	date_to: date_to ? dateRangeToPrimitive(date_to) : undefined,
})

type FilterState = ParamsType & { viewId?: string }

// Drží naposledy zvolený filtr seznamu, aby přežil odchod na detail poptávky
// a návrat zpět. Uchováváme v paměti (ne v URL), kvůli neserializovatelným
// hodnotám ve filtru (DateRange). Resetuje se až při plném reloadu stránky.
let lastFilter: FilterState = {}

export function Table() {
	const [params, setParams] = useList()
	const [filter, setFilterState] = useState<FilterState>(lastFilter)
	const setFilter: Dispatch<SetStateAction<FilterState>> = value => {
		setFilterState(prev => {
			const next = typeof value === 'function' ? value(prev) : value
			lastFilter = next
			return next
		})
	}
	const { viewId, ...rest } = filter

	const query = useObjectList('proposal', {
		...filterToPrimitive(rest),
		...params,
	})

	const { mutate: refuse } = useObjectActionMutationVariable('proposal', 'refuse')
	const { mutate: refuseElsewhere } = useObjectActionMutationVariable(
		'proposal',
		'refuse-elsewhere',
	)

	const onSetView = (viewId?: string, filter?: ParamsType) => {
		setFilter({ ...filter, viewId } as ParamsType & { viewId?: string })
	}

	return (
		<Paper>
			<div className="flex justify-between gap-4 px-5 pt-4 sm:px-6 sm:pt-5 print:hidden">
				<h3 className="text-md font-bold">Seznam poptávek</h3>
				<div className="flex items-center gap-4">
					<Input
						aria-label="Hledat klienta podle mailu, jména nebo telefonu"
						inputProps={{ placeholder: 'Hledat: mail / jméno / telefon' }}
						className="w-72"
						value={(filter.client_search ?? '') as string}
						onChange={(value: string) =>
							setFilter(
								prev =>
									({
										...prev,
										client_search: value === '' ? undefined : value,
									}) as ParamsType & { viewId?: string },
							)
						}
					/>
					<ButtonLink variant="PRIMARY" to="./create">
						Vytvořit
					</ButtonLink>
					<DialogTrigger>
						<Button variant="SECONDARY">Filtrovat</Button>
						<Sidepanel heading="Filtrovat">
							{({ close }) => (
								<FilteringSidepanel<ParamsType>
									object="proposal"
									onClose={close}
									filter={filter}
									onSubmit={setFilter}
									viewId={viewId}
									renderExtra={({ filter, setFilter }) => (
										<div className="grid grid-cols-2 gap-2">
											<Input
												label="Objem od (Kč)"
												type="number"
												value={(filter.volume_from ?? '') as string}
												onChange={(value: string) =>
													setFilter(
														prev =>
															({
																...prev,
																volume_from:
																	value === ''
																		? undefined
																		: value,
															}) as ParamsType,
													)
												}
											/>
											<Input
												label="Objem do (Kč)"
												type="number"
												value={(filter.volume_to ?? '') as string}
												onChange={(value: string) =>
													setFilter(
														prev =>
															({
																...prev,
																volume_to:
																	value === ''
																		? undefined
																		: value,
															}) as ParamsType,
													)
												}
											/>
										</div>
									)}
								/>
							)}
						</Sidepanel>
					</DialogTrigger>
				</div>
			</div>
			<Views onSetView={onSetView} viewId={viewId} filter={rest} object="proposal" />

			<List<GenericObject>
				id="proposal"
				params={params}
				setParams={setParams}
				query={query}
				header={
					<tr>
						<ListHeader sortBy="id">ID</ListHeader>
						<th>Poptané vozidlo</th>
						<ListHeader sortBy="date_from">Termín</ListHeader>
						<th>Pobočka [in/out]</th>
						<th>Klient</th>
						<th>Zdroj</th>
						<th>Cena / den</th>
						<ListHeader sortBy="pocet_dni">Dny</ListHeader>
						<ListHeader sortBy="volume_min">Objem</ListHeader>
						<th>Stav</th>
						<th className="print:hidden"></th>
						<th className="print:hidden"></th>
					</tr>
				}
			>
				{proposal => (
					<tr key={proposal.id}>
						<td>{proposal.id}</td>
						<td>
							<ProposalVehicleFormatter id={proposal.proposal_vehicle_id as string} />
							{proposal.poptane_vozidlo && (
								<div
									className="text-xs text-gray-600 mt-1 [&_a]:text-blue-600 [&_a]:underline [&_a]:hover:text-blue-800"
									dangerouslySetInnerHTML={{
										__html: proposal.poptane_vozidlo as string,
									}}
								/>
							)}
						</td>
						<td>
							<DateFormatter
								date={proposal.date_from as string}
								format="d.M. HH:mm"
							/>
							<br />
							<DateFormatter date={proposal.date_to as string} format="d.M. HH:mm" />
						</td>
						<td>
							<LocationFormatter id={proposal.location_from as string} />
							<br />
							<LocationFormatter id={proposal.location_to as string} />
						</td>
						<td>
							{proposal.client_id ? (
								<Link to={`/svc/client/${proposal.client_id}`}>
									<ClientFormatter id={proposal.client_id as string} />
								</Link>
							) : (
								'–'
							)}
						</td>
						<td>
							{proposal.source ? (
								<SourceFormatter source={proposal.source as string} />
							) : (
								'–'
							)}
						</td>
						<td>
							<CurrencyRange
								min={proposal.price_min as string | number}
								max={proposal.price_max as string | number}
							/>
						</td>
						<td>{proposal.pocet_dni as string}</td>
						<td>
							<CurrencyRange
								min={proposal.volume_min as string | number}
								max={proposal.volume_max as string | number}
							/>
						</td>
						<td>
							<span
								className={`px-2 rounded-xl text-xxs font-semibold ${STATUS_STYLES[proposal.status as keyof typeof STATUS_STYLES]}`}
							>
								{STATUS[proposal.status as keyof typeof STATUS_STYLES]}
							</span>
						</td>
						<td className="print:hidden flex gap-2" width="10%">
							<ButtonLink variant="SECONDARY" size="SMALL" to={`${proposal.id}`}>
								Detail
							</ButtonLink>
							{proposal.status === 'new' ? (
								<>
									<Button
										variant="DANGER"
										size="SMALL"
										onPress={() => refuse({ id: proposal.id })}
									>
										Fuck&nbsp;off
									</Button>
									<Button
										variant="DANGER"
										size="SMALL"
										onPress={() => refuseElsewhere({ id: proposal.id })}
									>
										Řešeno jinde/Fuck off
									</Button>
								</>
							) : null}
						</td>
					</tr>
				)}
			</List>

			<Outlet />
		</Paper>
	)
}
